/**
 * One work's record, as whoever may open it reads it: the identity block,
 * the photographs, the tabbed history, the agents panel, and the whole screen
 * assembled from the controller's state.
 *
 * The chain of title itself is `provenance.model.ts`'s; this file names the
 * surface and narrows the rows to a tab. The words are `work.constants.ts`'s,
 * and the reads and writes are `useWorkController.ts`'s. The public room in
 * `room/` borrows `identityRows` from here so Artist / Year / Medium read the
 * same on both.
 *
 * `identityRows` takes a work and `placementRows` takes a `PlacedWork`, and
 * the split is the boundary: a caller holding no placement cannot produce a
 * row naming a collection, a holder or a location, because it has nothing to
 * build one from. Four of the tabs are filters over one history, not records
 * of their own, and `toEntryRows` numbers the whole record before narrowing
 * so an entry keeps its ordinal under every tab.
 */

import type {
  AssignAgentRequest,
  AttributionState,
  CollectionSummary,
  ConsignmentArrangement,
  ConsignmentBasis,
  ProvenanceEntryKind,
  ProvenanceVerificationRequest,
  SetMandateScopeRequest,
  WorkAgent,
  WorkAttribution,
  WorkRepresentationResponse,
  WorkRepresentative,
  WorkCodeFacts,
  WorkEstateDisclosure,
  WorkState,
  WorkSummary,
  WorkAttributionControls,
  WorkCorrection,
  WorkCorrectionField,
  WorkVisibility,
  WorkVisibilityControls,
} from "@ondo/shared";
import { WORK_VISIBILITIES, attributionAnswered, isPublished } from "@ondo/shared";

import { describeApiError } from "../lib/apiError.ts";
import { formatDay } from "../lib/dates.ts";
import { parseMoneyMinor } from "../lib/money.ts";
import type { TagTone } from "../lib/Tag.tsx";
import { IMAGE_KIND_LABELS, WORK_COPY } from "../lib/constants.ts";
import { editionNumberOf, factsFromWorkRecord } from "@ondo/shared";
import type { CodeFacts } from "@ondo/shared";
import type { WorkPhotograph } from "../lib/workImages.ts";
import { describeLocation, toOwnerRef, workBadge } from "../lib/works.ts";
import type { EntityNames } from "../lib/works.ts";
import type { WorkBadge } from "../lib/works.ts";
import { toInsurancePanel } from "../insurance/insurance.model.ts";
import type { InsurancePanel } from "../insurance/insurance.model.ts";
import { MAX_ENTRIES } from "../provenance/provenance.constants.ts";
import { toAddHistoryView, toEntryRows } from "../provenance/provenance.model.ts";
import type {
  AddHistoryView,
  ClaimedArtist,
  ConfirmationLoad,
  DraftEntry,
  EntryRow,
  ProvenanceItem,
} from "../provenance/provenance.model.ts";
import { PROVENANCE_SURFACES } from "../provenance/provenance.surfaces.ts";
import {
  DEFAULT_WORK_TAB,
  ESTATE_DISCLOSURE_COPY,
  WORK_AGENT_COPY,
  WORK_REPRESENTATION_COPY,
  WORK_ATTRIBUTION_COPY,
  WORK_ATTRIBUTION_VISIBILITY_COPY,
  WORK_FIELD_LABELS,
  WORK_IMAGE_COPY,
  WORK_RECORD_COPY,
  WORK_TABS,
  WORK_TAB_EMPTY,
  WORK_TAB_HELP,
  WORK_VISIBILITY_COPY,
} from "./work.constants.ts";
import type { FilteringTab, WorkTabId } from "./work.constants.ts";

/**
 * A work together with the collection it was found in, WHEN THERE IS ONE.
 *
 * `collection` is null for every caller who reached this work without reaching
 * its placement — an agent under a mandate, somebody holding a single work
 * grant, the attributed artist. That is a refusal `work_placements_read` made,
 * not a gap in the record, and it is the same null `work.state` already carries
 * for the overlapping-but-not-identical set of callers who may not know where
 * the thing physically is.
 */
export interface PlacedWork {
  work: WorkSummary;
  collection: CollectionSummary | null;
  /**
   * The short identifier the owner reads off this screen and gives to an agent.
   *
   * ON `PlacedWork` AND NOT ON `WorkSummary`, which is what keeps it off every
   * card in every grid and — the case that matters — out of a viewing room. A
   * room is the one public surface in ONDO, and a visitor who could read a work
   * code off a published page could ask its owner for a mandate over it.
   * `ondo.viewing_room_contents()` names its columns one by one and this is not
   * among them; putting the code on the summary would have made that a matter
   * of that function staying careful rather than of the type.
   */
  ondoCode: string;
  /**
   * What the code's mark is coloured from, beside it for the same reason.
   *
   * Derived server-side on every read and carried across unchanged — see
   * `WorkCodeFacts`. Nothing on this side re-derives any of it: a colour
   * computed twice is a colour that can disagree with itself.
   */
  code: WorkCodeFacts;
  /**
   * WHETHER ANYBODY WITH AUTHORITY HAS SAID WHO MADE IT — OND-20.
   *
   * Null when the record carries no live authorship claim, which is the state
   * almost every work is in: nobody has written the claim down, so there is
   * nothing for a studio to have answered. Derived on every read by
   * `ondo.work_attribution()` and carried across unchanged, for the reason
   * `code` is: a verdict computed twice can disagree with itself, and this one
   * carries a studio's NAME.
   */
  attribution: WorkAttribution | null;
  /**
   * THE OWNER'S NAME, AS THE ATTRIBUTED ESTATE MAY SEE IT — OND-58.
   *
   * Null for every reader who is not the estate this work is attributed to,
   * AND when the owner has hidden it. The two are deliberately not
   * distinguishable: a gallery must not learn that an owner chose to hide, any
   * more than it learns the name.
   *
   * The consequence for this screen is that the row is drawn when there is a
   * name and omitted when there is not — see `estateOwnerRow`. The sentence
   * explaining that an owner WITHHELD it belongs on the catalogue, which knows
   * its reader is the estate; here nothing knows that.
   */
  ownerNameToEstate: string | null;
  /**
   * The owner's own two controls — OND-58. Null for anybody but the owner.
   *
   * The other direction from `ownerNameToEstate`: that is the name going out,
   * this is the pair of switches behind it coming back.
   */
  estateDisclosure: WorkEstateDisclosure | null;
  /**
   * How far the work travels — OND-47. Null for anybody but the owner.
   *
   * A THIRD CONTROL BESIDE THE TWO ABOVE rather than a widening of them. The
   * estate pair answers who the owner is to one counterparty; this answers how
   * wide an audience the work may have at all, and the combination that makes
   * them worth keeping apart is a work published at region whose owner is not
   * named to the estate.
   */
  visibility: WorkVisibilityControls | null;
  /**
   * What the ATTRIBUTED ESTATE controls about this work's public view —
   * OND-172. Null for everybody else, the OWNER included.
   *
   * The one field on this record the owner cannot see, and the only one that
   * belongs to a different party. OND-172 splits a work's public view along a
   * line that does not overlap: the artist, the year and whether the attribution
   * is contested are the estate's; the title, the medium, the photograph, the
   * location and whether the work is shown at all are the owner's. Two panels,
   * each null for the party it is not for, so neither party is ever shown a
   * control the server would refuse.
   */
  attributionControls: WorkAttributionControls | null;
  /** Every correction ever made to this work's catalogue fields — OND-41. */
  corrections: WorkCorrection[];
}

/**
 * What a corrected value carries beside it — OND-41.
 *
 * "it will always show as corrected so its not just true … it wil show the
 * error for ever and that it was corrected." So: what the record FIRST said
 * (the earliest correction's `fromValue`, not the latest — three corrections to
 * one field still show the original mistake), who made the latest correction,
 * and when. Already formatted, because this is what the view prints.
 */
export interface CorrectionMark {
  /** What the record said before anybody corrected it. "—" when it was empty. */
  from: string;
  by: string;
  when: string;
  /** How many times this field has been corrected. */
  times: number;
}

export interface IdentityRow {
  label: string;
  value: string;
  /** Present when the value is a corrected one; the mark the view must draw. */
  corrected?: CorrectionMark | null;
}

/**
 * What the identity block actually renders.
 *
 * Split out from `WorkIdentityModel` so a viewing room can build one. A room has
 * no owner to name and no placement to describe, and this is the shape that says
 * so — see `identityRows` and `placementRows` below for where the line is drawn.
 */
export interface WorkIdentityView {
  title: string;
  attribution: string;
  /**
   * Null where the registry has nothing to say to this reader.
   *
   * A viewing room is the only such place today: a public mark on a stranger's
   * chain of title is its own disclosure decision — see accessFGARules, "Public
   * attestation counts" — so a room shows no count in either direction. Null
   * rather than an empty label, because an empty badge is a badge claiming
   * nothing, and "nothing is claimed" and "no claim is shown" are different.
   */
  badge: WorkBadge | null;
  rows: IdentityRow[];
  /**
   * The title's own mark, when it has been corrected — OND-41. The title is
   * drawn as the heading rather than as a row, so its mark travels separately.
   * Absent on a viewing room, which carries no corrections.
   */
  titleCorrected?: CorrectionMark | null;
}

export interface WorkIdentityModel extends WorkIdentityView {
  badge: WorkBadge;
  /**
   * True when the location is withheld by policy rather than simply unset.
   *
   * There is no `owner` beside it. There was, and nothing ever read it —
   * `WorkIdentity` renders `rows`, and the holder reaches the screen through
   * `placementRows`. It went when `collection` became nullable rather than
   * being carried as an `OwnerRef | null` nobody would have consumed.
   */
  locationWithheld: boolean;
}

/**
 * Whether the reader may say where the work is.
 *
 * Owner of the holding collection — `ondo.set_location()` asks `owns_work`,
 * which is exactly that. A curator catalogues; they do not declare that a
 * picture has moved to a warehouse. Null placement is a reader who cannot see
 * the collection at all, who certainly may not.
 */
export function canSetLocation(placed: PlacedWork): boolean {
  return placed.collection?.level === "owner";
}

/**
 * Whether the reader may move the work or take it out — OND-22.
 *
 * The same predicate as `canSetLocation`, and deliberately a second name for
 * it: `ondo.move_work()` and `ondo.remove_work_from_collection()` both ask
 * `owns_collection` of the holding collection, which is exactly "owner of the
 * collection the record was reached through". A curator or a viewer at a firm
 * files nothing; an agent, a work-grant holder and the attributed estate reach
 * the work without reaching its placement and so see no panel at all.
 */
export function canMoveWork(placed: PlacedWork): boolean {
  return placed.collection?.level === "owner";
}

/** One collection the work could be moved into. */
export interface PlacementTarget {
  id: string;
  name: string;
}

/**
 * The placement panel, or null when the reader may not file this work.
 *
 * `targets` is the reader's OTHER collections at owner — the current one is not
 * a destination, and a collection held at curator or viewer is not one either,
 * because `ondo.move_work()` asks `owns_collection` of the receiving side too
 * and a list that offered it would offer a refusal. Null `collections` is the
 * list not having arrived yet, which the view draws as nothing to choose from
 * rather than as "no other collection".
 */
export interface PlacementPanel {
  current: { id: string; name: string };
  targets: PlacementTarget[];
  /** The list is still loading, so an empty `targets` says nothing yet. */
  loadingTargets: boolean;
  /** The reader pressed Remove once; the panel asks them to say it twice. */
  removeArmed: boolean;
}

export function toPlacementPanel(input: {
  placed: PlacedWork;
  collections: CollectionSummary[] | null;
  removeArmed: boolean;
}): PlacementPanel | null {
  const current = input.placed.collection;
  if (current === null || !canMoveWork(input.placed)) return null;

  return {
    current: { id: current.id, name: current.name },
    targets: (input.collections ?? [])
      .filter((collection) => collection.level === "owner" && collection.id !== current.id)
      .map((collection) => ({ id: collection.id, name: collection.name })),
    loadingTargets: input.collections === null,
    removeArmed: input.removeArmed,
  };
}

/**
 * What the catalogue says about the object.
 *
 * Nothing here is a placement fact. It takes a work rather than a `PlacedWork`,
 * which is the whole point: a caller holding only a work cannot accidentally
 * produce a row naming a collection, a holder or a location, because it has
 * nothing to build one from.
 *
 * `disclose.measurements` is for a viewing room that withholds them. The server
 * already omits the values in that case — this decides whether the ROWS appear
 * at all, because an empty cell reads as a gap in the record rather than as a
 * deliberate silence.
 */
export function identityRows(
  work: CatalogueFacts,
  disclose: { measurements: boolean } = { measurements: true },
  marks: CorrectionMarks = {},
): IdentityRow[] {
  const rows: IdentityRow[] = [
    // No mark on the artist, ever: authorship is not a correctable field
    // (`WORK_CORRECTION_FIELDS`), so there is nothing that could put one here.
    { label: WORK_FIELD_LABELS.artist, value: work.attributedName },
    { label: WORK_FIELD_LABELS.year, value: work.year ?? "—", corrected: marks.year ?? null },
    { label: WORK_FIELD_LABELS.medium, value: work.medium ?? "—", corrected: marks.medium ?? null },
  ];

  if (!disclose.measurements) return rows;

  return [
    ...rows,
    {
      label: WORK_FIELD_LABELS.dimensions,
      value: work.dimensions ?? "—",
      corrected: marks.dimensions ?? null,
    },
    { label: WORK_FIELD_LABELS.edition, value: describeEdition(work) },
  ];
}

/** The latest mark per corrected field; absent for a field never corrected. */
export type CorrectionMarks = Partial<Record<WorkCorrectionField, CorrectionMark>>;

/**
 * One mark per corrected field — OND-41.
 *
 * `corrections` arrive oldest first. The ORIGINAL value is the first
 * correction's `fromValue` — a field corrected three times still shows the
 * mistake the record was first made with, which is the owner's "for ever" —
 * and the corrector named is the latest, because that is who the current value
 * is owed to. A field with no correction has no mark, so a screen cannot draw
 * "corrected" over a value nobody touched.
 */
export function correctionMarks(corrections: WorkCorrection[]): CorrectionMarks {
  const marks: CorrectionMarks = {};
  for (const correction of corrections) {
    const existing = marks[correction.field];
    marks[correction.field] = {
      from: existing?.from ?? correction.fromValue ?? "—",
      by: correction.correctedBy.displayName,
      when: formatDay(correction.correctedAt),
      times: (existing?.times ?? 0) + 1,
    };
  }

  return marks;
}

/**
 * Whether the reader may correct a catalogue field — OND-41.
 *
 * Two parties, and the record tells the screen which it is talking to: the
 * owner of the holding collection (`collection.level === "owner"`, the rung
 * `ondo.correct_work()` asks through `owns_collection`), or the attributed
 * estate — the one reader for whom `attributionControls` is not null, because
 * `ondo.work_attribution_controls()` answers for the estate alone. The estate's
 * RUNG is the database's to check (editor); a viewer at the estate will see the
 * panel and be refused in the door's sentence, which the panel prints.
 */
export function canCorrectWork(placed: PlacedWork): boolean {
  return placed.collection?.level === "owner" || placed.attributionControls !== null;
}

/** One field the panel offers, with what the record says now. */
export interface CorrectionField {
  field: WorkCorrectionField;
  label: string;
  current: string;
}

/** The correction panel, or null when the reader may not correct this work. */
export interface CorrectionPanel {
  fields: CorrectionField[];
  field: WorkCorrectionField;
  value: string;
  /** The field chosen, as the record has it — what the typed value replaces. */
  current: string;
}

export function toCorrectionPanel(input: {
  placed: PlacedWork;
  field: WorkCorrectionField;
  value: string;
}): CorrectionPanel | null {
  if (!canCorrectWork(input.placed)) return null;

  const work = input.placed.work;
  const fields: CorrectionField[] = [
    { field: "title", label: WORK_FIELD_LABELS.title, current: work.title },
    { field: "year", label: WORK_FIELD_LABELS.year, current: work.year ?? "—" },
    { field: "medium", label: WORK_FIELD_LABELS.medium, current: work.medium ?? "—" },
    { field: "dimensions", label: WORK_FIELD_LABELS.dimensions, current: work.dimensions ?? "—" },
  ];

  return {
    fields,
    field: input.field,
    value: input.value,
    current: fields.find((each) => each.field === input.field)?.current ?? "—",
  };
}

/**
 * WHO OWNS IT, where the estate may be told — OND-58.
 *
 * Returns a row when the server sent a name and null otherwise, and there is no
 * third case: the decision of whether this caller may be told, and whether the
 * owner has permitted it, was made in SQL by `ondo.estate_owner_name()`. This
 * function has no condition of its own beyond "is there a name", which is the
 * property that makes the catalogue and this screen unable to disagree.
 *
 * It takes `PlacedWork`, so a viewing room cannot call it — the same structural
 * withholding `placementRows` relies on. A room's model carries no owner at
 * all, and `ondo.viewing_room_contents()` has no column that could.
 */
export function estateOwnerRow(placed: PlacedWork): IdentityRow | null {
  if (placed.ownerNameToEstate === null) return null;

  return { label: WORK_FIELD_LABELS.owner, value: placed.ownerNameToEstate };
}

/**
 * The owner's two estate controls, as the panel renders them — OND-58.
 *
 * Null for anybody who does not own the work, which is what the server answers
 * with rather than a pair of falses: a non-owner must not read an owner's
 * choices, and a panel drawn from defaults would show two switches that would
 * be refused if touched.
 *
 * `canGrant` is `hasEstate` rather than a copy of it, and the rename is the
 * point: the control is meaningless on a work nobody is the estate of, and the
 * screen says so instead of offering a switch with no circuit behind it. The
 * registry still does not tell the owner WHO the estate is — a boolean, never a
 * name.
 */
export interface EstateDisclosurePanel {
  /** The owner has hidden their name. False is the default — named unless hidden. */
  hidden: boolean;
  /** The owner has opened the whole record. False is the default. */
  recordOpen: boolean;
  /** Whether the work has an attributed estate for either control to reach. */
  canGrant: boolean;
  /** What the identity switch's current position means, in a sentence. */
  identityLine: string;
  /** What the record switch's current position means, in a sentence. */
  accessLine: string;
}

export function toEstateDisclosurePanel(placed: PlacedWork): EstateDisclosurePanel | null {
  const disclosure = placed.estateDisclosure;
  if (disclosure === null) return null;

  return {
    hidden: disclosure.identityHidden,
    recordOpen: disclosure.recordAccessGranted,
    canGrant: disclosure.hasEstate,
    // Four sentences for four states, and no sentence is shared between the two
    // controls — "naming the owner by default does not itself constitute a
    // grant of full record access", so a reader must never be able to take one
    // line as describing both.
    identityLine: disclosure.hasEstate
      ? disclosure.identityHidden
        ? ESTATE_DISCLOSURE_COPY.identityHidden
        : ESTATE_DISCLOSURE_COPY.identityShown
      : ESTATE_DISCLOSURE_COPY.noEstate,
    accessLine: disclosure.hasEstate
      ? disclosure.recordAccessGranted
        ? ESTATE_DISCLOSURE_COPY.accessGranted
        : ESTATE_DISCLOSURE_COPY.accessWithheld
      : ESTATE_DISCLOSURE_COPY.noEstate,
  };
}

/**
 * How far the work travels, as a list of choices rather than a value — OND-47.
 *
 * THE MODEL HANDS THE VIEW THE WHOLE RADIO GROUP, already decided: one option
 * per rung, each with its label, its sentence, whether it is the one in force,
 * and the one extra note that applies to it. The view maps and renders. A view
 * that held `visibility === "region" ? … : …` would be holding the product rule
 * about what each rung means, and there are four rungs and six sentences.
 *
 * `options` is built from `WORK_VISIBILITIES` rather than written out, and THAT
 * PROMISE WAS CASHED: OND-172 added the fourth rung to the enum and this screen
 * grew a fourth radio with no edit to this function at all — the copy maps below
 * went red until their fourth entry was written, which is exactly what
 * `Record<WorkVisibility, string>` is for.
 */
export interface VisibilityOption {
  value: WorkVisibility;
  title: string;
  line: string;
  selected: boolean;
  /**
   * A second sentence under this option alone. Only "Region only" has one, and
   * only on a work whose location carries no country — the setting is real and
   * discloses nothing, which is a fact about the location rather than about the
   * setting. "Anyone on ONDO" deliberately has none: a work with no photograph
   * still publishes its title, artist, year and medium, so the rung is doing
   * something and a warning would be false.
   */
  note: string | null;
}

/**
 * The location opt-in, decided — OND-172.
 *
 * A SHAPE AND NOT TWO BOOLEANS, so the view cannot be the thing that decides
 * when the warning appears or what the switch is currently doing. Both are
 * product rules: the warning belongs before the disclosure and the state
 * sentence has three cases, one of which ("on, and the work is not public") is
 * the one an owner would otherwise misread as broken.
 */
export interface LocationVisibilityControl {
  on: boolean;
  label: string;
  /** Shown while the switch is OFF — the moment it can still inform a choice. */
  warning: string | null;
  /** Shown while it is ON: what it is actually disclosing, which may be nothing. */
  state: string | null;
}

/**
 * The estate's panel, as the view renders it — OND-172.
 *
 * One switch and two sentences. The second sentence is about the CONTESTED mark,
 * which is derived from the work's history and is not a control here; it is on
 * the panel so that the absence of a switch for it reads as a design rather than
 * as something missing.
 */
export interface AttributionVisibilityPanel {
  on: boolean;
  label: string;
  /** What the switch is currently doing, including when it is doing nothing. */
  state: string;
  /** Whether a public view marks the attribution as disputed, and why. */
  contested: string;
}

export interface VisibilityPanel {
  options: VisibilityOption[];
  /** OND-172's separate per-work opt-in. Never implied by the rung above it. */
  location: LocationVisibilityControl;
  /**
   * The exception the owner cannot switch off, shown at every setting.
   *
   * A constant rather than a condition: the panel cannot be told whether this
   * artist has an estate — `has_estate` is on the estate panel and this one
   * answers a different question — so the sentence is written to be true
   * either way ("if this artist has an estate on ONDO").
   */
  exception: string;
}

export function toVisibilityPanel(placed: PlacedWork): VisibilityPanel | null {
  const controls = placed.visibility;
  if (controls === null) return null;

  const published = isPublished(controls.visibility);

  return {
    options: WORK_VISIBILITIES.map((value) => ({
      value,
      title: VISIBILITY_TITLES[value],
      line: VISIBILITY_LINES[value],
      selected: controls.visibility === value,
      // Two per-option notes, each hanging on its rung whether or not that rung
      // is the one selected — because an owner deciding whether to choose it
      // needs to know it discloses nothing BEFORE they choose it, not after.
      note: noteFor(value, controls),
    })),
    exception: WORK_VISIBILITY_COPY.estateException,
    // OND-172. The location opt-in, as a view-model rather than as a flag, so
    // the view never decides when the warning appears.
    location: {
      on: controls.locationPublic,
      label: WORK_VISIBILITY_COPY.locationLabel,
      // THE WARNING IS SHOWN WHILE THE SWITCH IS OFF, which is the only moment
      // it can do any good. OND-172 asks for "a plain warning at the moment it
      // is switched on, saying what it discloses"; a warning that appeared after
      // the disclosure had been made would be a receipt.
      warning: controls.locationPublic ? null : WORK_VISIBILITY_COPY.locationWarning,
      // What it is currently doing, which is three different things. Published
      // and on: the address is out there. On but the work is not public: the
      // choice stands and shows nobody anything. On with no location recorded:
      // the same, for a different reason the owner can fix.
      state: locationState(controls, published),
    },
  };
}

/**
 * The second sentence under one rung, or none.
 *
 * A function rather than a ternary inside the map because the question is which
 * rung is currently publishing nothing, and the answer names a remedy rather
 * than disabling the choice — 20261005098000 decision 14's reasoning, kept where
 * a second case would go if one arrives.
 */
function noteFor(
  value: WorkVisibility,
  controls: WorkVisibilityControls,
): string | null {
  return value === "region" && !controls.hasRegion
    ? WORK_VISIBILITY_COPY.regionMissing
    : null;
}

/**
 * The attributed estate's half of the public view, decided — OND-172.
 *
 * NULL FOR EVERYBODY ELSE, INCLUDING THE OWNER, and that is not this function's
 * decision: `ondo.work_attribution_controls()` returns no row for anybody but
 * the attributed estate, so the server hands this screen null and there is no
 * panel rather than a panel drawn disabled. The owner is never shown a control
 * they would be refused, which is OND-172's split made visible.
 *
 * THE CONTESTED LINE IS A STATEMENT, NOT A SWITCH, and the panel says so. An
 * estate contests an attribution by disputing the claim on the work's history,
 * in its own name; nothing stores a verdict and there is nothing here to press.
 * Without that sentence the panel looks as though a toggle is missing.
 */
export function toAttributionVisibilityPanel(
  placed: PlacedWork,
): AttributionVisibilityPanel | null {
  const controls = placed.attributionControls;
  if (controls === null) return null;

  return {
    on: controls.attributionPublic,
    label: WORK_ATTRIBUTION_VISIBILITY_COPY.label,
    state: controls.attributionPublic
      ? controls.workPublished
        ? WORK_ATTRIBUTION_VISIBILITY_COPY.publishedLive
        : WORK_ATTRIBUTION_VISIBILITY_COPY.publishedDormant
      : WORK_ATTRIBUTION_VISIBILITY_COPY.withheld,
    // Three sentences and the third is the one worth having: with the name
    // withheld there is no name to mark, so a public reader is told nothing
    // about the dispute either — and an estate that has just hidden the name
    // should learn that rather than discover it.
    contested: !controls.attributionPublic
      ? WORK_ATTRIBUTION_VISIBILITY_COPY.contestedWithheld
      : controls.contested
        ? WORK_ATTRIBUTION_VISIBILITY_COPY.contested
        : WORK_ATTRIBUTION_VISIBILITY_COPY.notContested,
  };
}

/** What the location opt-in is currently doing, in the owner's words. */
function locationState(
  controls: WorkVisibilityControls,
  published: boolean,
): string | null {
  if (!controls.locationPublic) return null;
  if (!controls.hasLocation) return WORK_VISIBILITY_COPY.locationMissing;
  if (!published) return WORK_VISIBILITY_COPY.locationDormant;
  return WORK_VISIBILITY_COPY.locationLive;
}

/**
 * The two label maps, keyed by the enum so a new rung cannot arrive without
 * copy. `Record<WorkVisibility, string>` is what makes that a type error.
 */
const VISIBILITY_TITLES: Record<WorkVisibility, string> = {
  private: WORK_VISIBILITY_COPY.privateTitle,
  named_parties: WORK_VISIBILITY_COPY.namedTitle,
  region: WORK_VISIBILITY_COPY.regionTitle,
  public: WORK_VISIBILITY_COPY.publicTitle,
};

const VISIBILITY_LINES: Record<WorkVisibility, string> = {
  private: WORK_VISIBILITY_COPY.privateLine,
  named_parties: WORK_VISIBILITY_COPY.namedLine,
  region: WORK_VISIBILITY_COPY.regionLine,
  public: WORK_VISIBILITY_COPY.publicLine,
};

/**
 * Where it sits and who holds it.
 *
 * Takes a `PlacedWork`, which is the point. The public room model has none — the
 * API returns a `PublicWork`, which carries no collection — so this function is
 * not merely unused there, it is UNCALLABLE. The withholding is structural
 * rather than a rule somebody has to remember.
 */
export function placementRows(
  placed: PlacedWork,
  entities: EntityNames = new Map(),
): IdentityRow[] {
  const location = describeLocation(placed.work);

  // One row saying so, rather than two rows of nothing or no rows at all. Same
  // argument as the location line below: a reader who is shown a work and no
  // placement should be told the placement was withheld, because a missing
  // block reads as a gap in the record rather than as a boundary.
  if (placed.collection === null) {
    return [
      { label: WORK_FIELD_LABELS.collection, value: WORK_COPY.collectionWithheld },
      { label: WORK_FIELD_LABELS.location, value: location === "" ? "Not recorded" : location },
    ];
  }

  return [
    { label: WORK_FIELD_LABELS.collection, value: placed.collection.name },
    // OND-52. THE ENTITY, WHEN THE READER CAN NAME IT. The ticket's third
    // complaint was that "a work's record cannot tell you which entity holds
    // it", and this is the line that could not: it said "Yours" of a company's
    // picture, and "Shared with you" of the same picture read from a viewer's
    // seat at that company. The map comes from the reader's own account and can
    // name nobody they could not already name, so an id it does not hold falls
    // back to exactly the two answers this row gave before.
    { label: WORK_FIELD_LABELS.holder, value: toOwnerRef(placed.collection, entities).label },
    // Never an empty cell: a withheld location is a refusal, and saying so
    // beats a blank that reads as a gap in the record.
    { label: WORK_FIELD_LABELS.location, value: location === "" ? "Not recorded" : location },
  ];
}

/** The subset of a work `identityRows` and `describeEdition` read. */
type CatalogueFacts = Pick<
  WorkSummary,
  "attributedName" | "year" | "medium" | "dimensions" | "editionText" | "editionSize"
>;

export function toIdentity(
  placed: PlacedWork,
  entities: EntityNames = new Map(),
): WorkIdentityModel {
  const work = placed.work;
  // OND-58. IT RIDES THE IDENTITY ROWS rather than getting a panel of its own,
  // and that is the point: `WorkIdentity` renders `rows`, so the owner's name
  // reaches this screen through the same one path every other catalogue fact
  // does. A second rendering path is how a name comes to appear on one screen
  // and not another, which is the bug the ticket is about.
  //
  // Between the catalogue facts and the placement, because that is what it is:
  // whose work this is sits after what the work is and before where it sits.
  const ownerRow = estateOwnerRow(placed);
  // OND-41. The marks ride the same rows every other catalogue fact does, for
  // the reason the owner's name does: one rendering path, so a corrected year
  // cannot be marked on one screen and plain on another.
  const marks = correctionMarks(placed.corrections);

  return {
    title: work.title,
    titleCorrected: marks.title ?? null,
    attribution: attributionOf(work),
    badge: workBadge(work),
    rows: [
      ...identityRows(work, { measurements: true }, marks),
      ...(ownerRow === null ? [] : [ownerRow]),
      ...placementRows(placed, entities),
    ],
    locationWithheld: work.state === null,
  };
}

/** 'Max Ernst, 1927', or just the name when the year is not recorded. */
export function attributionOf(work: Pick<WorkSummary, "attributedName" | "year">): string {
  return work.year ? `${work.attributedName}, ${work.year}` : work.attributedName;
}

/**
 * The authorship claim this work can support, or null.
 *
 * ONE PLACE, so the modal's prefill and the chip's availability cannot disagree
 * about whether a claim is possible. Null when the record holds only a typed
 * artist name: nobody is appointed over a string, so there would be no studio
 * the registry would accept an answer from, and an entry nobody can be asked
 * about is the one thing OND-20 must not produce.
 *
 * An EMPTY `attributedName` is null too. `ondo.works.attributed_name` is not
 * null by constraint, and a claim whose words were blank would be refused by
 * `validateEntry` after the collector had filled in the rest of the form.
 */
export function claimedArtistOf(
  work: Pick<WorkSummary, "artistId" | "attributedName">,
): ClaimedArtist | null {
  const name = work.attributedName.trim();
  if (work.artistId === null || name === "") return null;

  return { id: work.artistId, name };
}

/**
 * WHO MADE THIS, AND WHETHER ANYBODY WITH AUTHORITY AGREES — the panel.
 *
 * `claim` is the name in the words the claim was made in and `line` is the one
 * sentence about it; the view renders both and decides nothing.
 */
export interface AttributionPanelModel {
  title: string;
  /** Null only before any claim exists — there is no state word for 'no claim'. */
  tag: { label: string; tone: TagTone } | null;
  /** 'Erwin Wurm', frozen on the entry. Null when the record holds no claim. */
  claim: string | null;
  /** Said under the name whenever there is one: an attribution is not a yes. */
  claimNote: string | null;
  line: string;
  /** A second sentence, where the first one leaves an obvious question. */
  detail: string | null;
  /** Opens the modal on the Artist attribution chip. Null unless it would work. */
  ask: string | null;
  /** The claim's own row on the history, which is where the request door is. */
  entry: { id: string; label: string } | null;
}

/**
 * The attribution panel, from what `ondo.work_attribution()` returned.
 *
 * SEVEN OUTCOMES FROM SIX STATES, and the extra one is what this function
 * exists for. `not_requested` means two different things — nobody was ever
 * asked, and an authority WAS asked and ended the request without taking a
 * position — because an answer that takes no position writes no attestation and
 * the state falls back. Reading `state` alone would print "No studio has been
 * asked to confirm who made this" over a studio that read the request and
 * declined, which is not merely imprecise: it is the opposite of what happened,
 * and it is the sentence a buyer would act on. `lastOutcome` is on
 * `WorkAttribution` for this and `WORK_ATTRIBUTION_COPY.closed` is its words.
 *
 * NO NAME IS INVENTED. `answeredByName` is null in `not_requested` and
 * `awaiting`, and the type says so; the waiting sentence is written from
 * `requestedAt` instead. Nor is the party ASKED ever named, because the row does
 * not carry them — who was asked stays between the two of them.
 *
 * THE ASK IS OFFERED IN ONE PLACE ONLY: where there is no claim at all. Once a
 * claim exists, asking is a request against that entry and the entry's own row
 * is where that door is, so the panel sends the reader there rather than
 * growing a second way to do it that could disagree about which entry it meant.
 * And it is withheld, with its reason, from a work whose artist is free text —
 * authority is held over an artist the registry holds, so there would be nobody
 * the registry would accept an answer from.
 */
export function describeAttribution(input: {
  attribution: WorkAttribution | null;
  /** `WorkSummary.artistId`. Null when the record only holds a typed name. */
  artistId: string | null;
  /** Whether this seat may write the work's history at all. */
  canWriteHistory: boolean;
}): AttributionPanelModel {
  const copy = WORK_ATTRIBUTION_COPY;
  const live = input.attribution;

  if (live === null) {
    // Two different reasons for the same emptiness, and they are told apart:
    // one is fixable by asking and the other is not fixable from this screen.
    const askable = input.artistId !== null;

    return {
      title: copy.title,
      tag: null,
      claim: null,
      claimNote: null,
      line: copy.noClaim,
      detail: askable ? copy.noClaimDetail : copy.noClaimNoArtist,
      ask: askable && input.canWriteHistory ? copy.ask : null,
      entry: null,
    };
  }

  const answeredDay = formatDay(live.answeredAt, { utc: true });
  const name = live.answeredByName;

  return {
    title: copy.title,
    tag: { label: attributionTagLabel(live), tone: ATTRIBUTION_TONES[live.state] },
    claim: live.claimedArtistName,
    // THE SAME PREDICATE THE SENTENCE BELOW IT USES — OND-99. The note denied
    // that a confirmation existed while the line under it named the studio that
    // made one, so the two read as a contradiction to anybody who did not
    // already know the claim and the attestation are separate rows. One state
    // question, asked once, decides both.
    claimNote: attributionAnswered(live.state) ? copy.claimNoteAnswered : copy.claimNote,
    line: attributionLine(live, name, answeredDay),
    detail: null,
    // Never offered beside a live claim — see the header. The entry below is
    // the one door, and it is the door the database actually has.
    ask: null,
    entry: {
      id: live.entryId,
      label: attributionEntryLabel(live, input.canWriteHistory),
    },
  };
}

/**
 * The tag, which must not call an unanswered request "not confirmed" in the
 * same words it uses for a work nobody has mentioned to anybody.
 */
function attributionTagLabel(live: WorkAttribution): string {
  if (live.state === "not_requested" && live.lastOutcome !== null) {
    return WORK_ATTRIBUTION_COPY.tags.unanswered;
  }
  return WORK_ATTRIBUTION_COPY.tags[live.state];
}

/** The one sentence. `attributionAnswered` decides whether a name may appear. */
function attributionLine(
  live: WorkAttribution,
  name: string | null,
  day: string,
): string {
  const copy = WORK_ATTRIBUTION_COPY;

  if (attributionAnswered(live.state)) {
    // The predicate rather than `state !== "not_requested"`, which is what
    // `attributionAnswered` exists to stop: `awaiting` has no answer and no name
    // to print either. A name that somehow did not come back is said out loud
    // rather than printed as 'Confirmed by  on '.
    if (name === null) return copy.answeredUnnamed;

    // FOUR ANSWERED STATES SINCE OND-215, and the two tiers get different
    // sentences rather than the same sentence with a hedge in it. The domain
    // rides only on the unappointed pair, because `answeredByEmailDomain` is
    // null in every other reading by the database's own decision — see
    // `WorkAttribution`.
    switch (live.state) {
      case "confirmed":
        return copy.confirmed(name, day);
      case "disputed":
        return copy.disputed(name, day);
      case "confirmed_unappointed":
        return copy.confirmedUnappointed(name, day, live.answeredByEmailDomain);
      default:
        return copy.disputedUnappointed(name, day, live.answeredByEmailDomain);
    }
  }

  if (live.state === "awaiting") {
    const asked = formatDay(live.requestedAt, { utc: true });
    return asked === "" ? copy.awaitingUndated : copy.awaiting(asked);
  }

  // `not_requested`. The outcome is what tells the two situations apart, and
  // this is the line the whole of `describeAttribution` is written around.
  return live.lastOutcome === null ? copy.notRequested : copy.closed[live.lastOutcome];
}

/** Where the link goes, said as what pressing it is for. */
function attributionEntryLabel(live: WorkAttribution, canWriteHistory: boolean): string {
  const copy = WORK_ATTRIBUTION_COPY;

  // Only an author or curator of the entry may ask, so a reader who cannot
  // write the history is sent to READ the claim rather than told to ask.
  if (!canWriteHistory) return copy.openClaim;
  if (live.state === "not_requested") {
    return live.lastOutcome === null ? copy.openClaimToAsk : copy.openClaimToAskAgain;
  }
  return copy.openClaim;
}

/**
 * The tone per state. Not a judgement about the work — `disputed` is contested
 * because the registry is showing a disagreement, not resolving one.
 */
const ATTRIBUTION_TONES: Record<AttributionState, TagTone> = {
  not_requested: "unverified",
  awaiting: "pending",
  confirmed: "verified",
  disputed: "contested",
  // OND-215. `answer` is tag.css's "something that came back", and it is the
  // only honest tone for these two: `verified` and `contested` are the tones
  // for a fact THE REGISTRY STANDS BEHIND, and ONDO has appointed nobody here.
  // Both stances share it on purpose — the colour says an answer arrived, and
  // the label is what says which answer it was.
  confirmed_unappointed: "answer",
  disputed_unappointed: "answer",
};

/**
 * The edition, in one line.
 *
 * `editionText` is the collector's own words — '3/25' — and it wins where they
 * gave it. The size alone is a weaker statement and is written as one: knowing
 * an edition runs to 25 is not knowing which of the 25 this is.
 */
function describeEdition(work: CatalogueFacts): string {
  const text = work.editionText?.trim() ?? "";
  if (text !== "") return text;
  if (work.editionSize !== null) return `Edition of ${String(work.editionSize)}`;
  return "—";
}

/** What the photograph load has got to so far. */
export type PhotographLoad =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "loaded"; photographs: WorkPhotograph[]; urls: Map<string, string> };

export interface PhotographView {
  id: string;
  /** Null when storage refused the bytes. The tile stays; the picture does not. */
  url: string | null;
  caption: string;
  alt: string;
  selected: boolean;
}

export type WorkImagesModel =
  | { kind: "loading"; message: string }
  | { kind: "failed"; message: string }
  | { kind: "empty"; message: string }
  | { kind: "shown"; hero: PhotographView; thumbs: PhotographView[] };

/**
 * The photographs, as one hero and a strip beneath it.
 *
 * The selection is passed in rather than stored on each row, and an id that no
 * longer names a photograph falls back to the first one — which is what happens
 * on the first render, before anything has been chosen, and would otherwise
 * need a second effect to keep the two in step.
 *
 * A caption is the collector's own label where they gave one, because 'Detail /
 * condition' under six photographs says less than 'Lower left corner'.
 */
export function toImageViewer(input: {
  load: PhotographLoad;
  selectedId: string | null;
  title: string;
}): WorkImagesModel {
  if (input.load.kind === "loading") {
    return { kind: "loading", message: WORK_IMAGE_COPY.loading };
  }
  if (input.load.kind === "failed") {
    return { kind: "failed", message: WORK_IMAGE_COPY.failed };
  }

  const { photographs, urls } = input.load;
  const [first] = photographs;
  if (!first) return { kind: "empty", message: WORK_IMAGE_COPY.empty };

  const selected =
    photographs.find((photograph) => photograph.id === input.selectedId) ?? first;

  const toView = (photograph: WorkPhotograph): PhotographView => ({
    id: photograph.id,
    url: urls.get(photograph.objectPath) ?? null,
    caption: captionFor(photograph),
    alt: `${input.title} — ${captionFor(photograph)}`,
    selected: photograph.id === selected.id,
  });

  return { kind: "shown", hero: toView(selected), thumbs: photographs.map(toView) };
}

function captionFor(photograph: WorkPhotograph): string {
  const label = photograph.label?.trim() ?? "";
  return label === "" ? IMAGE_KIND_LABELS[photograph.kind] : label;
}

export interface WorkTabView {
  id: WorkTabId;
  label: string;
  active: boolean;
}

/**
 * What the panel under the tabs is.
 *
 * A union rather than the tab id, so the view never decides for itself which
 * tabs have been built — adding a filtering tab to WORK_TABS without copy in
 * WORK_TAB_HELP and WORK_TAB_EMPTY is a type error rather than a blank panel.
 * Both are keyed off WORK_TABS itself, so which table a new tab owes copy to
 * follows from its own `kinds`.
 *
 * THERE WAS A THIRD MEMBER, `not-yet`, AND OND-38 TOOK ITS LAST INSTANCE AWAY.
 * Catalogue Raisonné was the only tab that ever produced one; the citation is
 * the Significant publications tab, so nothing on this record is waiting on a
 * table and no tab can render a promise. The panel component survives for the
 * ASIDE panels that are still waiting — see `WORK_ASIDE_NOT_YET`.
 */
export type WorkPanel =
  /**
   * The whole record, or one kind of it.
   *
   * `kinds` null is Artwork history itself. A filtering tab carries the kinds it
   * shows, and `empty` is what it says when the record holds none of them — a
   * statement about the record rather than about the software, which is the
   * difference between this and a `not-yet`.
   *
   * Every tab has an `empty`, Artwork history included. It used to fall through
   * to the list's own four-word line, which said *provenance* on a tab called
   * Artwork history and — alone among the five — did not name the way in.
   */
  | {
      kind: "provenance";
      title: string;
      help: string;
      kinds: readonly ProvenanceEntryKind[] | null;
      empty: string;
    }
  /** The Record tab — OND-53. The panel owns its own read; see `WitnessPanel`. */
  | { kind: "witness"; title: string };

export function toTabs(active: WorkTabId): WorkTabView[] {
  return WORK_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    active: tab.id === active,
  }));
}

/** A tab that narrows. Narrows `tab.id` to the key the copy tables are typed on. */
function filters(tab: (typeof WORK_TABS)[number]): tab is FilteringTab {
  return tab.kinds !== null;
}

export function toPanel(active: WorkTabId): WorkPanel {
  const tab = WORK_TABS.find((candidate) => candidate.id === active) ?? WORK_TABS[0];

  if (tab.state === "witness") {
    return { kind: "witness", title: tab.label };
  }

  if (!filters(tab)) {
    return {
      kind: "provenance",
      title: WORK_RECORD_COPY.provenanceTitle,
      help: WORK_RECORD_COPY.provenanceHelp,
      kinds: null,
      empty: WORK_RECORD_COPY.provenanceEmpty,
    };
  }

  return {
    kind: "provenance",
    title: tab.label,
    // The kind's own sentence, then the one that says these are the same
    // rows. Composed here rather than written out four times.
    help: `${WORK_TAB_HELP[tab.id]} ${WORK_RECORD_COPY.filteredNote}`,
    kinds: tab.kinds,
    empty: WORK_TAB_EMPTY[tab.id],
  };
}

/**
 * Whether a panel shows entries of this kind.
 *
 * Answers for ONE entry rather than filtering a list, deliberately: it lets
 * `toEntryRows` number the whole record first and narrow it second, which is
 * what keeps a row's ordinal the same under every tab. See its `only` argument.
 */
export function panelShows(panel: WorkPanel, kind: ProvenanceEntryKind): boolean {
  if (panel.kind !== "provenance") return false;
  return panel.kinds === null || panel.kinds.includes(kind);
}

/**
 * Which tab a URL names.
 *
 * Exact match against WORK_TABS, like `parseRole` — a search parameter is
 * client-controlled input. Unlike `parseRole` this cannot return null: every
 * reading of a record is on some tab, so an unknown, stale or hand-edited
 * `?tab=` lands on the whole history rather than rendering nothing.
 *
 * `.some()` and not a lookup on an object of copy: `?tab=__proto__` against a
 * `Record<string, …>` resolves to something truthy, which is exactly the shape
 * of bug that puts a reader on a tab that does not exist.
 *
 * AN ABSENT `?tab=` IS THE DEFAULT, and that is stated here rather than at the
 * call site. `WorkRecord` used to carry a ternary reading
 * `namedTab === null && focusEntryId !== null ? DEFAULT_WORK_TAB : parseTab(namedTab)`
 * under twelve lines explaining that a link into one link of the chain must not
 * open a tab that would hide the row it points at — but both branches evaluate
 * to the same thing, because a null never matches a tab id. The rule was true
 * only by coincidence, and would have stopped being true the moment this
 * fallback moved. One fact, in one place.
 */
export function parseTab(raw: string | null): WorkTabId {
  return WORK_TABS.some((tab) => tab.id === raw) ? (raw as WorkTabId) : DEFAULT_WORK_TAB;
}

/**
 * What `?tab=` should say, or null where the default needs no parameter.
 *
 * The default state of the screen keeps the clean address, so a URL copied off
 * Artwork history is the same URL a plain link to the work produces.
 */
export function tabParam(tab: WorkTabId): string | null {
  return tab === DEFAULT_WORK_TAB ? null : tab;
}

/** See `describeApiError`; kept under this name for its controllers and tests. */
export function describeError(error: unknown): string {
  return describeApiError(error, WORK_RECORD_COPY.unknownError);
}

// ── Who represents this work ─────────────────────────────────────────────────

/**
 * The agent panel, as one value the view renders without deciding anything.
 *
 * A discriminated union rather than a list plus flags, because "hidden" and
 * "empty" are genuinely different answers and a component holding both a
 * `canAssign` boolean and an `agents` array would have to work out which of the
 * four combinations it was in.
 *
 * `hidden` is the one worth naming. It is NOT "you have no agents" — it is the
 * server saying `capabilities.assignAgent` is false, which today means the
 * caller owns no work and therefore has nothing to appoint anyone over. A
 * curator of somebody else's collection sees this record and no panel, and that
 * is correct rather than a gap.
 */
export type AgentPanel =
  | { kind: "hidden" }
  | { kind: "loading"; message: string }
  | { kind: "listed"; agents: WorkAgent[]; canAssign: boolean };

/**
 * Who represents this work, as a VIEWER reads it — OND-73.
 *
 * `hidden` for two different reasons that must render identically: the caller
 * manages this work's agents (the owner's panel above already lists them, and
 * two panels saying "Represented by" would be the same fact twice), or the
 * answer was empty — which is what a work with no representative AND a caller
 * the work does not reach both get, and the screen must not tell those apart.
 *
 * AND THE SAME FACT IS DROPPED ROW BY ROW, not only panel by panel — OND-216.
 * "Manages" was too narrow a test for "the panel above already says this": a
 * seat at the REPRESENTING firm cannot assign, so this panel showed, and
 * `ondo.work_agents()` still answers a member their own firm's mandate, so the
 * panel above showed too. A cataloguer read one appointment twice. Dropping the
 * duplicated rows rather than the whole panel is what keeps the half of OND-73
 * that only this panel has — an ENDED or EXPIRED appointment, drawn as what it
 * is — visible to the firm that lived through it.
 */
export type RepresentationPanel =
  | { kind: "hidden" }
  | { kind: "loading"; message: string }
  | { kind: "listed"; current: WorkRepresentative[]; past: WorkRepresentative[] };

export function toRepresentationPanel(input: {
  representation: WorkRepresentationResponse | null;
  /** The owner's side: `capabilities.assignAgent`, which already draws the agents panel. */
  managesAgents: boolean;
  /**
   * What the agents panel is already drawing, so this one never repeats it.
   * Keyed on the MANDATE and not the principal: a second, separate appointment
   * of the same agent is a different fact and must still be shown.
   */
  listedAgents: WorkAgent[] | null;
}): RepresentationPanel {
  if (input.managesAgents) return { kind: "hidden" };
  if (input.representation === null) {
    return { kind: "loading", message: WORK_REPRESENTATION_COPY.loading };
  }

  const drawnAbove = new Set((input.listedAgents ?? []).map((row) => row.mandateId));
  const unrepeated = (rows: WorkRepresentative[]) =>
    drawnAbove.size === 0 ? rows : rows.filter((row) => !drawnAbove.has(row.mandateId));

  const current = unrepeated(input.representation.current);
  const past = unrepeated(input.representation.past);
  if (current.length === 0 && past.length === 0) return { kind: "hidden" };

  return { kind: "listed", current, past };
}

/**
 * The dates line under a representative's name, from the record and never from
 * arithmetic: "since 3 March", "since 3 March · until 1 June", "ended 4 May",
 * "expired 1 June". The standing decides which word, and the standing is
 * SQL's — see `WorkRepresentative.standing`.
 */
export function describeRepresentationTerm(row: WorkRepresentative): string {
  const since = WORK_REPRESENTATION_COPY.since(formatDay(row.startedAt));
  if (row.standing === "current") {
    return row.expiresAt
      ? `${since} · ${WORK_REPRESENTATION_COPY.until(formatDay(row.expiresAt))}`
      : since;
  }
  if (row.standing === "expired") {
    return `${since} · ${WORK_REPRESENTATION_COPY.expired(formatDay(row.expiresAt ?? row.startedAt))}`;
  }
  return `${since} · ${WORK_REPRESENTATION_COPY.ended(formatDay(row.endedAt ?? row.startedAt))}`;
}

/**
 * Pure, and total on the three inputs that decide it.
 *
 * Note what does NOT appear: any test of whether a principal is an agent. That
 * question is answered once, by `ondo.principal_capacities()`, and reaches this
 * screen only as a verdict on a response. A copy of the rule here would be the
 * third — see the migration header.
 */
export function toAgentPanel(input: {
  agents: WorkAgent[] | null;
  canAssign: boolean;
  /** True for the first load only; a refresh keeps the list on screen. */
  loading: boolean;
}): AgentPanel {
  // The panel is about who represents the work, and a caller who cannot assign
  // is not entitled to the list either — `ondo.work_agents()` returns nothing to
  // them, so an empty panel would be indistinguishable from "no agent" and would
  // report a fact the policy declined to disclose.
  if (!input.canAssign && (input.agents === null || input.agents.length === 0)) {
    return { kind: "hidden" };
  }

  if (input.agents === null) {
    return { kind: "loading", message: WORK_AGENT_COPY.loading };
  }

  return { kind: "listed", agents: input.agents, canAssign: input.canAssign };
}

/** The assign modal, in the four states it can be on screen in. */
export interface AssignAgentView {
  open: boolean;
  ondoId: string;
  note: string;
  /**
   * The term, as a `<input type="date">` holds it: 'YYYY-MM-DD' or empty.
   *
   * EMPTY IS OPEN-ENDED, which is the default and what every appointment made
   * before this field existed is. The dialog does not pre-fill a date and does
   * not offer a "6 months" shortcut: a term is an agreement between two people,
   * and a suggested one is the product having an opinion about somebody else's
   * arrangement.
   */
  expiresOn: string;
  /**
   * Whether this agent may appoint other agents.
   *
   * ONE CHECKBOX, unticked, which is the owner's recorded decision: "When the
   * collector assigns an agent they should be able to say they allow agent
   * delegation. If so, the agent can assign other agents. If not, the work is
   * locked to that agent only."
   */
  mayDelegate: boolean;
  /**
   * Whether to offer the box at all — and THE ONE PLACE THIS SCREEN DOES NOT
   * MIRROR THE DATABASE EXACTLY.
   *
   * `canEnd` and `canRenew` are computed by `ondo.work_agents()` from the same
   * expressions the two write functions test, so the screen cannot offer a
   * control the database will refuse. This flag cannot manage that yet: the
   * party who may appoint but may not grant delegation ONWARD is an agent acting
   * under a delegable mandate, and telling them apart from an owner needs a
   * per-work, per-caller answer nothing returns today.
   *
   * So it is wired to the appoint permission, the box is offered, and a delegate
   * who ticks it is refused in the database's own words — the arrangement the
   * ONDO-id check already runs on. Closing the gap means a `can_delegate_onward`
   * column on `ondo.work_agents()`, which is a read shape belonging with
   * whichever screen needs it to be exact.
   */
  canOfferDelegation: boolean;
  /**
   * The organisation to include beside the named agent, as the field holds it.
   *
   * EMPTY IS "AGENT ONLY", which is the default and the narrower of OND-83's two
   * choices: colleagues do not gain access just because they work at the same
   * firm. The dialog does not pre-fill the agent's own firm and must not — the
   * firm is NAMED by the appointer, never derived from a membership, and
   * pre-filling it would make the wider choice the one a collector got by not
   * reading the field.
   */
  organisationOndoId: string;
  /**
   * Whether this appointment may agree a consignment for the client.
   *
   * UNTICKED, which is OND-82: "Appointment alone must not automatically grant
   * this power." Unlike `mayDelegate` there is no `canOffer…` flag beside it,
   * and the absence is not an oversight: a delegate who ticks this without
   * holding it themselves is refused BY NAME in SQL ("an agent cannot grant a
   * consignment permission they were not given themselves"), which the dialog
   * already prints — the same arrangement the ONDO-id check runs on.
   */
  mayConsign: boolean;
  /** Shape only, and about the organisation field. See `malformed`. */
  malformedOrganisation: boolean;
  /** The agreed terms, as the form holds them: strings, parsed on submit. */
  terms: AgentTermsDraft;
  /**
   * What is wrong with the terms as typed, or null.
   *
   * THE TWO THE FORM CAN ANSWER AND NO MORE. A floor above the asking price and
   * a commission beside a fee are both decidable from the draft alone, and both
   * are check constraints that would come back as a 422 with a constraint name
   * in it. Everything else — whether the id exists, whether the work is already
   * represented, whether a basis of `min_price_agreed` has its floor — is the
   * database's, and `consign.model.ts` draws the line in the same place.
   */
  problem: string | null;
  /**
   * What the server said about the id, or null before it has been asked.
   *
   * `verdict` is the server's word and is never computed here. `message` is the
   * server's sentence too — the SAME one the write raises — so a collector who
   * ignores the check and presses the button does not get two explanations of
   * one refusal.
   */
  check:
    | null
    | { state: "checking" }
    | { state: "eligible"; displayName: string }
    | { state: "refused"; message: string };
  /** True while the write is in flight. Distinct from `checking`. */
  busy: boolean;
  /** Shape only. See `WORK_AGENT_COPY.malformed`. */
  malformed: boolean;
  /** Whether Assign should be pressable at all. */
  canSubmit: boolean;
}

/**
 * The terms half of the appoint form, held as the inputs hold it.
 *
 * STRINGS, AND PARSED ON SUBMIT, which is `consign.model.ts`'s `OfferDraft`
 * shape and for its reason: a number input that holds a number cannot hold the
 * state "the user has typed a decimal point and nothing after it". `currency`
 * defaults to GBP there too.
 *
 * NAMED `agreedAsking` AND `agreedFloor` even here, where it would be easy to
 * shorten. OND-43's distinction is the thing most likely to be lost in a rename,
 * and the draft is where a later reader is most likely to do it: these are the
 * ADVISOR's figures, and a gallery consignment's effective floor is a different
 * number on a different table.
 */
export interface AgentTermsDraft {
  arrangement: ConsignmentArrangement | "";
  basis: ConsignmentBasis | "";
  /** A percentage as typed — '15', '15.25'. Converted to basis points on submit. */
  commission: string;
  fee: string;
  agreedAsking: string;
  agreedFloor: string;
  currency: string;
}

export const EMPTY_AGENT_TERMS: AgentTermsDraft = {
  arrangement: "",
  basis: "",
  commission: "",
  fee: "",
  agreedAsking: "",
  agreedFloor: "",
  currency: "GBP",
};

/**
 * The draft, as the request body.
 *
 * Returns `null` for the terms when nothing was typed, so the commonest
 * appointment sends no `terms` object at all rather than seven nulls.
 *
 * COMMISSION IS A PERCENTAGE IN AND BASIS POINTS OUT, and the rounding is
 * `Math.round` on purpose: '15.255' becomes 1526 rather than a fraction of a
 * basis point, because `commission_bp` is an integer column and the alternative
 * is a 422 about a type.
 */
export function toAgentTerms(draft: AgentTermsDraft): AssignAgentRequest["terms"] {
  const commissionBp =
    draft.commission.trim() === "" ? null : Math.round(Number(draft.commission) * 100);
  const terms = {
    arrangement: draft.arrangement === "" ? null : draft.arrangement,
    basis: draft.basis === "" ? null : draft.basis,
    commissionBp: Number.isFinite(commissionBp) ? commissionBp : null,
    feeMinor: parseMoneyMinor(draft.fee),
    agreedAskingMinor: parseMoneyMinor(draft.agreedAsking),
    agreedFloorMinor: parseMoneyMinor(draft.agreedFloor),
    currency: draft.currency.trim().toUpperCase() || null,
  };

  const stated =
    terms.arrangement !== null ||
    terms.basis !== null ||
    terms.commissionBp !== null ||
    terms.feeMinor !== null ||
    terms.agreedAskingMinor !== null ||
    terms.agreedFloorMinor !== null;

  return stated ? terms : null;
}

/**
 * What the form itself can tell the user is wrong.
 *
 * See `AssignAgentView.problem` for why these two and no others.
 */
export function agentTermsProblem(draft: AgentTermsDraft): string | null {
  const asking = parseMoneyMinor(draft.agreedAsking);
  const floor = parseMoneyMinor(draft.agreedFloor);

  if (asking !== null && floor !== null && floor > asking) {
    return WORK_AGENT_COPY.floorOverAsking;
  }

  if (draft.commission.trim() !== "" && parseMoneyMinor(draft.fee) !== null) {
    return WORK_AGENT_COPY.commissionAndFee;
  }

  return null;
}

/** Shape, and nothing else. The server decides everything about existence. */
export function isOndoIdShape(value: string): boolean {
  return /^ONDO-[0-9]{4,}$/i.test(value.trim());
}

/**
 * THE FOUR NEW FIELDS ARE OPTIONAL HERE, AND DEFAULTED TO THE PRODUCT'S OWN
 * DEFAULTS, which is the same sentence the schema's column defaults make: no
 * term, no delegation, no agreed terms. A caller that says nothing about a term
 * gets an open-ended appointment, which is what every appointment made before
 * these fields existed is.
 *
 * It is not laziness about the call sites. `AssignAgentView` itself keeps all
 * four REQUIRED, so the dialog cannot read an undefined draft — the optionality
 * is on the way IN, where "I have nothing to say about a term" is a real thing
 * for a caller to mean.
 */
export function toAssignAgentView(input: {
  open: boolean;
  ondoId: string;
  note: string;
  expiresOn?: string;
  mayDelegate?: boolean;
  canOfferDelegation?: boolean;
  organisationOndoId?: string;
  mayConsign?: boolean;
  terms?: AgentTermsDraft;
  check: AssignAgentView["check"];
  busy: boolean;
}): AssignAgentView {
  const typed = input.ondoId.trim().length > 0;
  const malformed = typed && !isOndoIdShape(input.ondoId);
  const terms = input.terms ?? EMPTY_AGENT_TERMS;
  const canOfferDelegation = input.canOfferDelegation ?? false;
  const problem = agentTermsProblem(terms);

  // THE SAME SHAPE TEST AS THE AGENT'S OWN ID, on the same grounds: whether the
  // id exists, is an organisation, or is the agent just named is answered by ONE
  // refusal in SQL, and the only thing this screen may say about an id is that
  // it could not possibly be one.
  const organisationOndoId = input.organisationOndoId ?? "";
  const malformedOrganisation =
    organisationOndoId.trim().length > 0 && !isOndoIdShape(organisationOndoId);

  return {
    ...input,
    expiresOn: input.expiresOn ?? "",
    terms,
    canOfferDelegation,
    organisationOndoId,
    mayConsign: input.mayConsign ?? false,
    malformedOrganisation,
    // A delegate cannot pass delegation on, so a ticked box that the caller may
    // not grant is forced back to false here rather than being sent and refused.
    mayDelegate: canOfferDelegation && (input.mayDelegate ?? false),
    malformed,
    problem,
    // Deliberately NOT gated on `check.state === "eligible"`. The check is a
    // courtesy that can fail, be slow, or be skipped by pressing Enter, and a
    // button that stayed dead because a background request never returned would
    // be worse than one that submits and is refused by the write. The server
    // refuses either way; this only stops an obvious typo.
    //
    // IT *IS* GATED ON `problem`, and the difference is which side knows the
    // answer. An ONDO id's validity is the server's to judge; a floor above an
    // asking price is arithmetic the form has already done, and submitting it
    // would spend a round trip to be told something the screen could see.
    canSubmit:
      typed && !malformed && !malformedOrganisation && problem === null && !input.busy,
  };
}

/**
 * Who an appointment reaches, for the row that shows it.
 *
 * BOTH ANSWERS ARE SAID OUT LOUD, including the narrow one — see
 * `WORK_AGENT_COPY.agentOnly` for why the absence of a firm is not allowed to be
 * the way a collector learns their colleagues are excluded.
 *
 * THE FIRM'S NAME AND NOT ITS ONDO ID, because this line is read by the person
 * who chose the firm and a name is what they chose it by. The id is on the
 * appointment and reaches the screen on `scope.organisation.ondoId` for anywhere
 * that needs to name it back.
 */
export function describeAgentScope(agent: WorkAgent): string {
  return agent.scope.organisation === null
    ? WORK_AGENT_COPY.agentOnly
    : WORK_AGENT_COPY.alsoReaches(agent.scope.organisation.displayName);
}

/**
 * A scope being edited on one appointment's row.
 *
 * STRINGS AND A BOOLEAN, as the inputs hold them — `AgentTermsDraft`'s shape and
 * for its reason. `organisationOndoId` empty means the named agent alone, which
 * is the same thing it means on the appoint form and the same thing a null means
 * on the wire.
 */
export interface ScopeDraft {
  organisationOndoId: string;
  mayConsign: boolean;
  /**
   * OND-79. Whether the appointment may change the owner's disclosure rules.
   *
   * IN THE DRAFT BESIDE `mayConsign` AND NOT ON A PANEL OF ITS OWN, because the
   * scope is one statement: `POST /scope` replaces both permissions and the
   * organisation together, so a control that sent one without reading the other
   * would revoke whichever it had not been told about.
   */
  mayManageDisclosure: boolean;
}

/**
 * The draft an appointment's row starts from: what it records now.
 *
 * SO THE FORM OPENS ON THE TRUTH rather than on empty fields. A scope is STATED
 * WHOLE by `POST /scope` — absent organisation REMOVES one — so a form that
 * opened blank and was saved unchanged would silently narrow a grant. Seeding it
 * from the appointment is what makes "save" mean "this is the scope" rather than
 * "apply what I typed".
 */
export function scopeDraftOf(agent: WorkAgent): ScopeDraft {
  return {
    organisationOndoId: agent.scope.organisation?.ondoId ?? "",
    mayConsign: agent.scope.mayConsign,
    mayManageDisclosure: agent.scope.mayManageDisclosure,
  };
}

/**
 * Whether a draft differs from what the appointment records.
 *
 * The database refuses a change that changes nothing — "that is the scope this
 * appointment already has" — so the screen says so first rather than spending a
 * round trip to be told something it could see. The same line
 * `toAssignAgentView` draws between the two kinds of problem: an id's validity
 * is the server's to judge; whether two values differ is not.
 */
export function scopeChanged(agent: WorkAgent, draft: ScopeDraft): boolean {
  const current = scopeDraftOf(agent);

  return (
    current.mayConsign !== draft.mayConsign ||
    current.mayManageDisclosure !== draft.mayManageDisclosure ||
    current.organisationOndoId.trim().toUpperCase() !==
      draft.organisationOndoId.trim().toUpperCase()
  );
}

/**
 * The draft as the request body.
 *
 * `null` RATHER THAN AN EMPTY STRING for an absent organisation, because on this
 * route null is a value: it removes the firm. An empty string would be refused
 * by `ondoIdField()` as a malformed id, which is a different answer from "the
 * named agent alone".
 */
export function toScopeRequest(draft: ScopeDraft): SetMandateScopeRequest {
  const organisation = draft.organisationOndoId.trim().toUpperCase();

  return {
    mayConsign: draft.mayConsign,
    mayManageDisclosure: draft.mayManageDisclosure,
    organisationOndoId: organisation === "" ? null : organisation,
  };
}

export type WorkRecordView =
  | { kind: "loading"; message: string }
  | { kind: "not-found"; message: string }
  | { kind: "error"; message: string }
  | {
      kind: "open";
      identity: WorkIdentityModel;
      /** The work's code, for the panel that hands it to an agent. */
      ondoCode: string;
      /**
       * What the code's mark is coloured from.
       *
       * Assembled here rather than in the view, and read off the server's own
       * derived facts rather than re-derived: `ondo.work_record()` answered, and
       * a second opinion computed in TypeScript is how the two drift apart.
       */
      codeFacts: CodeFacts;
      /**
       * Who made it, and whether anybody with authority agrees — OND-20.
       *
       * Beside `codeFacts` and not folded into it, which is the split the API
       * keeps for the same reason: the mark's facts are caller-blind and this
       * carries an authority's NAME.
       */
      attribution: AttributionPanelModel;
      /**
       * The owner's two estate controls — OND-58. Null for anybody else, so a
       * screen that has no panel to draw cannot draw one.
       */
      estatePanel: EstateDisclosurePanel | null;
      /**
       * How far the work travels — OND-47. Null for anybody else, by the same
       * rule as the panel above: the server returns no row for a non-owner, so
       * there is nothing to draw rather than a panel drawn disabled.
       */
      visibilityPanel: VisibilityPanel | null;
      /**
       * OND-172. The ESTATE's half of the public view, and null for every other
       * reader — the owner included. Nobody ever sees both panels.
       */
      attributionVisibilityPanel: AttributionVisibilityPanel | null;
      images: WorkImagesModel;
      tabs: WorkTabView[];
      /** The open tab, for the `aria-labelledby` that names the panel below it. */
      activeTab: WorkTabId;
      panel: WorkPanel;
      rows: EntryRow[];
      openEntryId: string | null;
      refusals: { entryId: string; sentences: string[] } | null;
      full: boolean;
      busy: boolean;
      /** The Add-to-artwork-history modal, mounted once by the screen. */
      dialog: AddHistoryView;
      /** Who represents this work. `hidden` when the caller may not assign. */
      agentPanel: AgentPanel;
      /**
       * The same question for everybody else — OND-73. `hidden` when the
       * caller manages the agents (the panel above says) or when the answer
       * was empty, which a work with no representative and a caller the work
       * does not reach both get.
       */
      representationPanel: RepresentationPanel;
      /**
       * Who insures it, derived from the work's own history — OND-48.
       *
       * `hidden` when no period of cover stands. Not a stored field and not a
       * second endpoint: the rows it is read off are the same ones the tabs are
       * drawn from, which is what makes the panel and the Artwork history
       * incapable of disagreeing about a policy.
       */
      insurancePanel: InsurancePanel;
      /** The assign modal, mounted once by the screen beside the other. */
      assignAgent: AssignAgentView;
      /**
       * Where the work is, as the record says — for the location panel, which
       * is where an owner changes it. Null when withheld or never recorded.
       */
      location: WorkState | null;
      /**
       * Whether the reader may say where the work is: owner of the holding
       * collection, which is the rung `ondo.set_location()` asks for. Per
       * WORK, unlike the account-level capability hints, because the record
       * knows which collection it sits in and at what level.
       */
      canSetLocation: boolean;
      /**
       * Which collection holds the work and where else it could go — OND-22.
       * Null for everybody but the owner of the holding collection, which is
       * the rung both placement doors ask for.
       */
      placementPanel: PlacementPanel | null;
      /**
       * Correct a catalogue field — OND-41. Null for everybody but the owner of
       * the holding collection and the attributed estate.
       */
      correctionPanel: CorrectionPanel | null;
      /** Failure of an action, as distinct from failure to load the screen. */
      error: string | null;
    };
/**
 * The whole screen, from the controller's state.
 *
 * Pure, and the one place the surface is named and the tab's filter applied —
 * see the comments inside. Lives here rather than in the hook so the record's
 * every rendering decision can be asserted without React.
 */
export function resolveWorkRecordView(input: {
  /**
   * The entities the reader acts for, keyed by ONDO id — OND-52.
   *
   * OPTIONAL, and omitted means the two-state holder line every caller had
   * before: a reader whose entities have not been resolved is shown "Yours" or
   * "Shared with you" rather than a name that might be wrong. `entityNamesOf`
   * builds it out of the account read, so nothing here looks anything up.
   */
  entities?: EntityNames;
  placed: PlacedWork | null;
  workId: string;
  items: ProvenanceItem[];
  openEntryId: string | null;
  refusals: { entryId: string; sentences: string[] } | null;
  busy: boolean;
  error: string | null;
  loadError: string | null;
  photographs: PhotographLoad;
  selectedImageId: string | null;
  tab: WorkTabId;
  confirmations: Record<string, ConfirmationLoad>;
  asks: Record<string, ProvenanceVerificationRequest[]>;
  /**
   * The seat's rung, threaded in so the ATTRIBUTION PANEL can decide whether to
   * offer the ask rather than leaving the view to combine a prop with a model
   * answer. See `WorkRecord`'s `canWriteHistory` for what it is.
   */
  canWriteHistory: boolean;
  dialogOpen: boolean;
  draft: DraftEntry | null;
  agents: WorkAgent[] | null;
  canAssignAgent: boolean;
  /**
   * Who represents the work, as a viewer reads it — OND-73. Null until asked;
   * the panel hides itself for the owner, whose agents panel already says.
   */
  representation: WorkRepresentationResponse | null;
  /**
   * The reader's collections, for the placement panel's destinations — OND-22.
   * Null until they have been asked for, which the owner's panel does and
   * nobody else's needs to.
   */
  collections: CollectionSummary[] | null;
  /** Whether Remove has been pressed once and awaits its second press. */
  removeArmed: boolean;
  /** The correction panel's draft — which field, and what it should say — OND-41. */
  correctionField: WorkCorrectionField;
  correctionValue: string;
  agentOpen: boolean;
  agentId: string;
  agentNote: string;
  agentExpiresOn: string;
  agentMayDelegate: boolean;
  agentOrganisationId: string;
  agentMayConsign: boolean;
  agentTerms: AgentTermsDraft;
  agentCheck: AssignAgentView["check"];
}): WorkRecordView {
  if (input.loadError !== null) return { kind: "error", message: input.loadError };
  if (!input.placed) return { kind: "loading", message: WORK_RECORD_COPY.loading };

  // Not found and not permitted are the same answer, deliberately: telling them
  // apart would say whether a work with that id exists. Both arrive here as a
  // 404 caught into `loadError` above; this branch is the load that has not
  // failed and has not returned, which cannot happen and is cheap to state.
  const found = input.placed;
  if (!found) return { kind: "not-found", message: WORK_RECORD_COPY.notFound };

  const panel = toPanel(input.tab);

  return {
    kind: "open",
    identity: toIdentity(found, input.entities),
    ondoCode: found.ondoCode,
    // The agent band is drawn only when somebody actually holds a mandate —
    // `hands` is true of every work, and a band on every work would say
    // nothing. The panel below already knows; this reads its answer.
    codeFacts: factsFromWorkRecord(found.code, {
      hasAgent: (input.agents?.length ?? 0) > 0,
      editionNumber: editionNumberOf(found.work.editionText),
      editionSize: found.work.editionSize,
    }),
    attribution: describeAttribution({
      attribution: found.attribution,
      artistId: found.work.artistId,
      canWriteHistory: input.canWriteHistory,
    }),
    // OND-58. Null for anybody but the owner, which is the server's answer and
    // not a judgement made here — a non-owner gets no row from
    // `ondo.work_estate_controls()` at all.
    estatePanel: toEstateDisclosurePanel(found),
    // OND-47. Null for anybody but the owner, for the same reason the line
    // above is: `ondo.work_visibility_controls()` returns no row at all rather
    // than returning the default, so a non-owner cannot read how far somebody
    // else's work travels off their own screen.
    visibilityPanel: toVisibilityPanel(found),
    attributionVisibilityPanel: toAttributionVisibilityPanel(found),
    images: toImageViewer({
      load: input.photographs,
      selectedId: input.selectedImageId,
      title: found.work.title,
    }),
    tabs: toTabs(input.tab),
    activeTab: input.tab,
    panel,
    // The surface is named once, here. Everything the work record shows
    // differently from the register wizard follows from that one word.
    //
    // DRAFTS ARE NOT IN THIS LIST. The modal owns the one being composed, and
    // the Provenance tab shows the record — a half-typed entry sitting in the
    // timeline between two things the registry actually holds would read as one
    // of them. A tab that filters narrows the same list by kind.
    //
    // TWO FILTERS, ON OPPOSITE SIDES OF THE NUMBERING. That a draft is not part
    // of the record is a statement about the record, so it is gone before the
    // rows are counted. That a tab shows one kind is a statement about the tab,
    // so it is applied after — which is what makes one entry read 'Entry 9 of
    // 14' on Documents and on Artwork history alike.
    rows: toEntryRows(
      input.items.filter((item) => item.kind === "recorded"),
      PROVENANCE_SURFACES.record,
      { confirmations: input.confirmations, asks: input.asks },
      (item) => panelShows(panel, item.entry.kind),
    ),
    openEntryId: input.openEntryId,
    refusals: input.refusals,
    full: input.items.length >= MAX_ENTRIES,
    busy: input.busy,
    dialog: toAddHistoryView({
      open: input.dialogOpen,
      subject: [found.work.title, found.work.attributedName]
        .filter((part) => part !== null && part !== "")
        .join(" · "),
      draft: input.draft,
      // THE CLAIM IS READ OFF THE WORK AND NEVER TYPED. Both halves or neither:
      // the id is what makes the claim answerable — an authority is appointed
      // over an `ondo.artists` row — and the words are what the entry freezes.
      // A work carrying only a typed artist name supplies nothing, and the chip
      // is withheld with its reason rather than offering a claim no studio
      // could ever answer.
      claim: claimedArtistOf(found.work),
      surface: PROVENANCE_SURFACES.dialog,
      refusals: input.refusals?.sentences ?? [],
      problem: input.error,
      busy: input.busy,
      finalNote: WORK_RECORD_COPY.saveIsFinal,
    }),
    agentPanel: toAgentPanel({
      agents: input.agents,
      canAssign: input.canAssignAgent,
      loading: input.agents === null,
    }),
    representationPanel: toRepresentationPanel({
      representation: input.representation,
      managesAgents: input.canAssignAgent,
      listedAgents: input.agents,
    }),
    // The WHOLE record and not the tab's filtered rows: the panel is beside the
    // object, not inside a reading of its history, so which tab happens to be
    // open must not change what it says about cover.
    insurancePanel: toInsurancePanel(input.items),
    location: found.work.state,
    canSetLocation: canSetLocation(found),
    placementPanel: toPlacementPanel({
      placed: found,
      collections: input.collections,
      removeArmed: input.removeArmed,
    }),
    correctionPanel: toCorrectionPanel({
      placed: found,
      field: input.correctionField,
      value: input.correctionValue,
    }),
    assignAgent: toAssignAgentView({
      open: input.agentOpen,
      ondoId: input.agentId,
      note: input.agentNote,
      expiresOn: input.agentExpiresOn,
      mayDelegate: input.agentMayDelegate,
      // THE SAME FLAG THAT GATES THE APPOINT BUTTON, and the honest reason is
      // that the screen cannot do better. `canAssignAgent` comes from
      // `ondo.my_capabilities().assign_agent`, which is true for an owner, a
      // consigned gallery AND an agent allowed to delegate — and the one party
      // who may appoint but may NOT grant delegation onward is that third one.
      // Telling them apart needs a per-work, per-caller answer nothing returns
      // today.
      //
      // So the box is offered and the database has the last word, which is the
      // same arrangement the ONDO-id check already runs on: a delegate who ticks
      // it meets 'an agent appointed by delegation cannot pass delegation on',
      // and `translate` carries that exact sentence to the dialog. That is a
      // weaker contract than `canEnd` and `canRenew` keep, and it is recorded as
      // such rather than papered over — see `AssignAgentView.canOfferDelegation`.
      canOfferDelegation: input.canAssignAgent,
      // OND-83 and OND-82, AND NEITHER GETS A `canOffer…` FLAG. The reason is
      // not the one above: the recipient choice is every appointer's to make,
      // and the consignment permission is refused BY NAME in SQL when a
      // delegator does not hold it — 'an agent cannot grant a consignment
      // permission they were not given themselves' — which `translate` carries
      // to this dialog verbatim. So there is no state the screen could be in
      // that the database would not explain.
      organisationOndoId: input.agentOrganisationId,
      mayConsign: input.agentMayConsign,
      terms: input.agentTerms,
      check: input.agentCheck,
      busy: input.busy,
    }),
    error: input.error,
  };
}
